import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Badge } from '@vaiu/ui/badge';
import { Separator } from '@vaiu/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { 
  ArrowLeft, 
  Gift, 
  Users, 
  Clock, 
  CheckCircle, 
  Plus,
  Copy,
  Share2,
  AlertCircle,
  Info,
  Calendar,
  DollarSign
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@vaiu/ui/select";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@vaiu/ui/alert";
import { platformApi } from '@/services/platformApi';

interface ReferralToken {
  id: string;
  code: string;
  amount: number;
  currency: string;
  maxRedeems: number;
  currentRedeems: number;
  status: 'active' | 'expired' | 'exhausted';
  expiryDate: string;
  createdAt: string;
  creditOriginator: boolean;
}

const ReferralTokensPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [referralTokens, setReferralTokens] = useState<ReferralToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Create form state
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [maxRedeems, setMaxRedeems] = useState('10');
  const [expiryDays, setExpiryDays] = useState('30');
  const [creditOriginator, setCreditOriginator] = useState(true);

  useEffect(() => {
    loadReferralTokens();
  }, []);

  const loadReferralTokens = async () => {
    setLoading(true);
    try {
      // Use real API to get user's referral tokens
      const result = await platformApi.getReferralTokens({
        page: 1,
        limit: 20,
        status: 'all'
      });
      
      // Transform backend data to frontend format
      const transformedTokens: ReferralToken[] = result.tokens.map(token => ({
        id: token.tokenId || token.id,
        code: token.tokenKey,
        amount: token.amount,
        currency: token.currency,
        maxRedeems: 1, // Referral tokens are typically single-use
        currentRedeems: token.isRedeemed ? 1 : 0,
        status: token.status === 'redeemed' ? 'exhausted' : token.status,
        expiryDate: token.expiresIn ? new Date(Date.now() + token.expiresIn).toISOString() : '',
        createdAt: token.createdAt ? new Date(token.createdAt).toLocaleDateString() : '',
        creditOriginator: token.rewardTriggered || token.isNewUserReferral || false
      }));
      
      setReferralTokens(transformedTokens);
    } catch (error: any) {
      console.error('Failed to load referral tokens:', error);
      
      // Check if it's a 404 or 405 error (endpoint not implemented)
      if (error.message?.includes('404') || error.message?.includes('405') || 
          error.message?.includes('Not Found') || error.message?.includes('Method Not Allowed')) {
        // Backend endpoint not ready - show empty state
        setReferralTokens([]);
        // Don't show error toast for unimplemented endpoints
        console.log('Referral tokens endpoint not yet implemented');
      } else {
        // Show error for actual failures
        toast({
          title: 'Error',
          description: 'Failed to load referral tokens. Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReferralToken = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount greater than 0',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Use real API to create referral token
      const result = await platformApi.createReferralToken({
        amount: parseFloat(amount),
        currency,
        message: `Referral token for ${amount} ${currency}`
      });

      toast({
        title: 'Referral Token Created!',
        description: `Token ${result.key} created successfully. Share it to earn rewards!`,
      });

      // Reset form
      setAmount('');
      setMaxRedeems('10');
      setExpiryDays('30');
      setCreditOriginator(true);
      setShowCreateForm(false);
      
      // Reload tokens
      loadReferralTokens();
    } catch (error: any) {
      console.error('Failed to create referral token:', error);
      toast({
        title: 'Creation Failed',
        description: error.message || 'Failed to create referral token. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copied!',
        description: 'Referral code copied to clipboard',
      });
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const shareReferralCode = async (code: string, amount: number, currency: string) => {
    const shareText = `Join me on Vaiu and get ${currency} ${amount}! Use referral code: ${code}`;
    const shareUrl = `${window.location.origin}/redeem/${code}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Vaiu',
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        await copyToClipboard(`${shareText}\n${shareUrl}`);
      }
    } else {
      await copyToClipboard(`${shareText}\n${shareUrl}`);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'expired':
        return <Badge variant="secondary">Expired</Badge>;
      case 'exhausted':
        return <Badge variant="outline">Exhausted</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Referral Tokens</h1>
              <p className="text-sm text-gray-600">Create and manage your referral codes</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New
          </Button>
        </div>

        {/* How it Works */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>How Referral Tokens Work</AlertTitle>
          <AlertDescription className="mt-2">
            <div className="space-y-2 text-sm">
              <p>• <strong>Create referral codes</strong> that can be redeemed multiple times</p>
              <p>• <strong>Set the reward amount</strong> and maximum number of uses</p>
              <p>• <strong>Choose if you get rewarded</strong> when someone uses your code</p>
              <p>• <strong>Share your codes</strong> to recruit new users to the platform</p>
              <p>• <strong>Track redemptions</strong> and see how many people joined through your referrals</p>
            </div>
          </AlertDescription>
        </Alert>

        {/* Create Form */}
        {showCreateForm && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Gift className="w-5 h-5" />
                <span>Create Referral Token</span>
              </CardTitle>
              <CardDescription>
                Create a multi-use referral code that others can redeem to join the platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="amount">Reward Amount</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="amount"
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0.01"
                      placeholder="10.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Amount each user gets when they redeem your code
                  </p>
                </div>

                <div>
                  <Label htmlFor="maxRedeems">Maximum Uses</Label>
                  <Select value={maxRedeems} onValueChange={setMaxRedeems}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 uses</SelectItem>
                      <SelectItem value="10">10 uses</SelectItem>
                      <SelectItem value="25">25 uses</SelectItem>
                      <SelectItem value="50">50 uses</SelectItem>
                      <SelectItem value="100">100 uses</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    How many people can use this code
                  </p>
                </div>

                <div>
                  <Label htmlFor="expiry">Expires In</Label>
                  <Select value={expiryDays} onValueChange={setExpiryDays}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                      <SelectItem value="365">1 year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="creditOriginator"
                    checked={creditOriginator}
                    onChange={(e) => setCreditOriginator(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="creditOriginator" className="text-sm">
                    I also get rewarded when someone uses my code
                  </Label>
                </div>
              </div>

              <div className="flex space-x-2 pt-4">
                <Button 
                  onClick={handleCreateReferralToken}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? 'Creating...' : 'Create Referral Token'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateForm(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Existing Referral Tokens */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="w-5 h-5" />
              <span>Your Referral Tokens</span>
            </CardTitle>
            <CardDescription>
              Manage your existing referral codes and track their performance
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading referral tokens...</p>
              </div>
            ) : referralTokens.length === 0 ? (
              <div className="text-center py-8">
                <Gift className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No referral tokens yet</p>
                <p className="text-sm text-gray-400">Create your first referral code to start inviting friends!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {referralTokens.map((token) => (
                  <div key={token.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="font-mono text-lg font-bold">{token.code}</div>
                        {getStatusBadge(token.status)}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(token.code)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => shareReferralCode(token.code, token.amount, token.currency)}
                        >
                          <Share2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <span>{token.currency} {token.amount}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span>{token.currentRedeems}/{token.maxRedeems} used</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-purple-600" />
                        <span>Expires {new Date(token.expiryDate).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {token.creditOriginator ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-gray-400" />
                        )}
                        <span>{token.creditOriginator ? 'You get rewarded' : 'No self-reward'}</span>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Usage Progress</span>
                        <span>{Math.round((token.currentRedeems / token.maxRedeems) * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(token.currentRedeems / token.maxRedeems) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Statistics Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5" />
              <span>Referral Statistics</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-purple-600">{referralTokens.length}</div>
                <div className="text-sm text-gray-500">Total Codes</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {referralTokens.reduce((sum, token) => sum + token.currentRedeems, 0)}
                </div>
                <div className="text-sm text-gray-500">Total Redemptions</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {referralTokens.filter(token => token.status === 'active').length}
                </div>
                <div className="text-sm text-gray-500">Active Codes</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-pink-600">
                  {referralTokens.reduce((sum, token) => 
                    sum + (token.creditOriginator ? token.currentRedeems * token.amount : 0), 0
                  ).toFixed(2)}
                </div>
                <div className="text-sm text-gray-500">Earned (USD)</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReferralTokensPage; 